import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { X, Search, RotateCcw, MessageSquare, Database } from 'lucide-react';

const Header = ({
  isMobile,
  setMobileSearchVisible,
  onClose,
  setIsSearchOpen,
  darkMode,
  onResetConversation,
  onToggleHistoryPanel,
  isHistoryPanelOpen,
  isSearchOpen,
  isKnowledgePanelOpen,
  setIsKnowledgePanelOpen
}) => {
  return (
    <Box sx={{
      p: 2,
      borderBottom: '1px solid',
      borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: "64px",
      backdropFilter: "blur(8px)",
      bgcolor: darkMode ? 'rgba(30,30,30,0.8)' : 'rgba(255,255,255,0.8)',
      zIndex: 10
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        {!isMobile && (
          <Tooltip title={isHistoryPanelOpen ? "Hide chat history" : "Show chat history"}>
            <IconButton
              onClick={onToggleHistoryPanel}
              aria-label="Toggle chat history"
              sx={{
                color: isHistoryPanelOpen ? 'primary.main' : 'inherit',
                transition: 'all 0.2s',
                mr: 1,
                width: 40,
                height: 40,
                bgcolor: isHistoryPanelOpen ? 
                  (darkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)') : 
                  (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'),
                '&:hover': { 
                  transform: 'scale(1.05)', 
                  bgcolor: darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'
                },
              }}
            >
              <MessageSquare size={22} />
            </IconButton>
          </Tooltip>
        )}
        <Typography variant="h6" sx={{ 
          fontWeight: 600,
          background: darkMode 
            ? 'linear-gradient(90deg, #0EA5E9 0%, #22C55E 100%)'
            : 'linear-gradient(90deg, #0284C7 0%, #16A34A 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textFillColor: 'transparent'
        }}>
          {isMobile ? 'AIssistant' : 'AIssistant Interno'}
        </Typography>
        {!isMobile && (
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: 0.35,
              borderRadius: '999px',
              bgcolor: darkMode ? 'rgba(59,130,246,0.2)' : 'rgba(37,99,235,0.12)',
              color: darkMode ? 'rgba(191,219,254,0.95)' : 'rgba(30,64,175,0.95)',
              border: '1px solid',
              borderColor: darkMode ? 'rgba(59,130,246,0.45)' : 'rgba(37,99,235,0.35)',
              letterSpacing: '0.01em',
              fontWeight: 600,
            }}
          >
            Workflow: Knowledge → Dati → Catalogo
          </Typography>
        )}
      </Box>
      
      <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
        {isMobile ? (
          <>
            <Tooltip title="Cerca">
              <IconButton 
                onClick={() => setMobileSearchVisible(true)}
                sx={{
                  bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  borderRadius: '8px',
                  '&:hover': { 
                    bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                  },
                }}
              >
                <Search size={20} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Chiudi">
              <IconButton 
                onClick={onClose}
                sx={{
                  bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  borderRadius: '8px',
                  '&:hover': { 
                    bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                  },
                }}
              >
                <X size={20} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title={isSearchOpen ? "Nascondi ricerca" : "Mostra ricerca"}>
              <IconButton
                onClick={() => setIsSearchOpen(prev => !prev)}
                aria-label="Toggle knowledge panel"
                sx={{
                  color: isSearchOpen ? 'primary.main' : 'inherit',
                  bgcolor: isSearchOpen ? 
                    (darkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)') : 
                    (darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
                  borderRadius: '8px',
                  '&:hover': { 
                    bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                  },
                }}
              >
                <Search size={20} />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={isKnowledgePanelOpen ? "Nascondi file" : "Mostra file"}>
              <IconButton
                onClick={() => setIsKnowledgePanelOpen(prev => !prev)}
                aria-label="Toggle files panel"
                sx={{
                  color: isKnowledgePanelOpen ? 'primary.main' : 'inherit',
                  bgcolor: isKnowledgePanelOpen ? 
                    (darkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)') : 
                    (darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
                  borderRadius: '8px',
                  '&:hover': { 
                    bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                  },
                }}
              >
                <Database size={20} />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Nuova chat">
              <IconButton
                onClick={onResetConversation}
                aria-label="Reset conversation"
                sx={{
                  bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  borderRadius: '8px',
                  '&:hover': { 
                    bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                  },
                }}
              >
                <RotateCcw size={20} />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Chiudi">
              <IconButton 
                onClick={onClose}
                aria-label="Close"
                sx={{
                  ml: 1,
                  bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  borderRadius: '8px',
                  '&:hover': { 
                    bgcolor: darkMode ? 'rgba(220,38,38,0.2)' : 'rgba(220,38,38,0.1)',
                    color: 'error.main'
                  },
                }}
              >
                <X size={20} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Header; 
